import { c as _c } from "react/compiler-runtime";
import * as React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSeedling } from '@fortawesome/pro-regular-svg-icons';
import { Typography } from '@components/typography';
import { LOW_INVENTORY_THRESHOLD } from './constants';
export type LowInventoryNoticeVariant = 'low' | 'zero' | 'zero-international';
interface LowInventoryNoticeProps {
  variant: LowInventoryNoticeVariant;
  totalCount: number;
  hasFiltersApplied: boolean;
  hasDates: boolean;
  className?: string;
}
interface NoticeContent {
  heading?: string;
  headline?: string;
  body: string;
}
function getContent({
  variant,
  hasFiltersApplied,
  hasDates
}: {
  variant: LowInventoryNoticeVariant;
  hasFiltersApplied: boolean;
  hasDates: boolean;
}): NoticeContent | null {
  const hasNarrowingInputs = hasFiltersApplied || hasDates;
  switch (variant) {
    case 'low':
      return hasNarrowingInputs ? {
        headline: 'Just a few matches right now.',
        body: "Try widening your filters, or check back soon. We're adding properties fast."
      } : {
        headline: "You're seeing everything we have here right now.",
        body: 'Savvy is new and adding properties fast. Check back soon for more.'
      };
    case 'zero':
      return hasNarrowingInputs ? {
        heading: 'NO RESULTS',
        body: 'Nothing matches your search yet. Try clearing a filter or your dates, or check back soon as we add more places.'
      } : {
        heading: 'NO RESULTS',
        body: 'No properties yet, but Savvy is new and growing fast. Check back soon, or peek at a nearby destination.'
      };
    case 'zero-international':
      return {
        heading: 'NO RESULTS',
        body: "Savvy is new, and we're still mostly in the U.S. Try a U.S. destination, or check back as we expand."
      };
    default:
      return null;
  }
}

/**
 * Low-inventory and zero-results traveler messaging for property search.
 *
 * Three variants:
 * - `low` — small banner above the grid when `0 < totalCount < LOW_INVENTORY_THRESHOLD`
 * - `zero` — full empty-state body for U.S. queries with no results
 * - `zero-international` — full empty-state body for non-U.S. queries
 *
 * A search emptied by the platform booking lead time is not one of these: it
 * says nothing about inventory or country, so it renders `CheckInTooSoonNotice`
 * instead (BHT-8058).
 *
 * Renders nothing if `low` is requested with a count outside the low band.
 */
export const LowInventoryNotice: React.FC<LowInventoryNoticeProps> = t0 => {
  const $ = _c(23);
  const {
    variant,
    totalCount,
    hasFiltersApplied,
    hasDates,
    className
  } = t0;
  if (variant === "low" && !(totalCount > 0 && totalCount < LOW_INVENTORY_THRESHOLD)) {
    return null;
  }
  let t1;
  if ($[0] !== hasDates || $[1] !== hasFiltersApplied || $[2] !== variant) {
    t1 = getContent({
      variant,
      hasFiltersApplied,
      hasDates
    });
    $[0] = hasDates;
    $[1] = hasFiltersApplied;
    $[2] = variant;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const content = t1;
  if (!content) {
    return null;
  }
  if (variant === "low") {
    const t2 = className ?? "mx-2 mb-6 mt-2 sm:mx-2";
    let t3;
    if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
      t3 = <div className="flex-shrink-0 flex h-10 w-10 items-center justify-center rounded-full bg-pool"><FontAwesomeIcon icon={faSeedling} className="text-base text-primary-main" /></div>;
      $[4] = t3;
    } else {
      t3 = $[4];
    }
    let t4;
    if ($[5] !== content.headline) {
      t4 = content.headline && <Typography variant="body1" component="p" className="text-primary-main font-semibold">{content.headline}</Typography>;
      $[5] = content.headline;
      $[6] = t4;
    } else {
      t4 = $[6];
    }
    let t5;
    if ($[7] !== content.body) {
      t5 = <Typography variant="body2" component="p" className="text-primary-main mt-0.5">{content.body}</Typography>;
      $[7] = content.body;
      $[8] = t5;
    } else {
      t5 = $[8];
    }
    let t6;
    if ($[9] !== t4 || $[10] !== t5) {
      t6 = <div className="flex items-center gap-4 rounded-2xl bg-pool/40 px-5 py-4 ring-1 ring-pool">{t3}<div className="flex-1 min-w-0">{t4}{t5}</div></div>;
      $[9] = t4;
      $[10] = t5;
      $[11] = t6;
    } else {
      t6 = $[11];
    }
    let t7;
    if ($[12] !== t2 || $[13] !== t6) {
      t7 = <div data-id="@search/low-inventory-notice" className={t2}>{t6}</div>;
      $[12] = t2;
      $[13] = t6;
      $[14] = t7;
    } else {
      t7 = $[14];
    }
    return t7;
  }
  const t2 = className ?? "pl-4 sm:pl-8 pt-16 sm:py-16 w-full text-gray-dark";
  let t3;
  if ($[15] !== content.heading) {
    t3 = content.heading && <div className="text-xl font-semibold">{content.heading}</div>;
    $[15] = content.heading;
    $[16] = t3;
  } else {
    t3 = $[16];
  }
  let t4;
  if ($[17] !== content.body) {
    t4 = <div className="text-lg font-medium pt-6">{content.body}</div>;
    $[17] = content.body;
    $[18] = t4;
  } else {
    t4 = $[18];
  }
  let t5;
  if ($[19] !== t2 || $[20] !== t3 || $[21] !== t4) {
    t5 = <div data-id="@search/low-inventory-notice" className={t2}>{t3}{t4}</div>;
    $[19] = t2;
    $[20] = t3;
    $[21] = t4;
    $[22] = t5;
  } else {
    t5 = $[22];
  }
  return t5;
};